import {normalizeEmail} from '@sollapay/utils';

import {matchesMemberSearch} from '@/features/team/utils';

import {isValidShareEmail} from './share-chips.utils';

import type {TrustAccountMemberIneligibleReason, TrustAccountMemberRole} from '@sollapay/enums';
import type {TrustAccountMemberCandidateDTO} from '@sollapay/types';

const MAX_MEMBER_SUGGESTIONS = 5;

/** A member suggestion carries a reason only when the firm user can hold neither role. */
export type ShareSuggestion =
  | {
      email: string;
      kind: 'member';
      person: TrustAccountMemberCandidateDTO;
      ineligibleReason: TrustAccountMemberIneligibleReason | null;
    }
  | {email: string; kind: 'guest'};

/**
 * Firm users whose name or email matches the draft, and the draft itself as a guest when it could be
 * one. An exact address comes first, so Enter never swaps a typed email for someone else.
 * `candidatesByEmail` is keyed by normalized email.
 */
export function getShareSuggestions(params: {
  draft: string;
  role: TrustAccountMemberRole;
  candidatesByEmail: ReadonlyMap<string, TrustAccountMemberCandidateDTO>;
  chippedEmails: ReadonlySet<string>;
  accessEmails: ReadonlySet<string>;
}): ShareSuggestion[] {
  const {role, candidatesByEmail, chippedEmails, accessEmails} = params;
  const query = params.draft.trim();
  if (!query) return [];
  const typedEmail = normalizeEmail(query);
  const isOpen = (email: string) => !chippedEmails.has(email) && !accessEmails.has(email);

  const members: ShareSuggestion[] = [...candidatesByEmail]
    .filter(([email, person]) => isOpen(email) && matchesMemberSearch(person, query))
    .sort(([a], [b]) => Number(b === typedEmail) - Number(a === typedEmail))
    .slice(0, MAX_MEMBER_SUGGESTIONS)
    .map(([email, person]) => ({
      email,
      kind: 'member',
      person,
      ineligibleReason: Object.values(person.ineligibleReasons).every(Boolean)
        ? person.ineligibleReasons[role]
        : null
    }));

  const isGuestOffer =
    isValidShareEmail(typedEmail) && !candidatesByEmail.has(typedEmail) && isOpen(typedEmail);
  return isGuestOffer ? [{email: typedEmail, kind: 'guest'}, ...members] : members;
}
